import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { AllProductMatchedProduct } from "../components/QuestionInput";

type AllProductValidationStage = "idle" | "loading" | "confirm" | "select";
export type SuggestionsLoadingPhase = "search" | "score" | "apply" | "skip" | null;

export type SuggestionsActionIntent = { type: "selectProduct"; product: AllProductMatchedProduct } | { type: "continueWithAll" } | { type: "cancel" };

export interface BrowsableSubProduct {
    manual_sub_key: string;
    faq_sub_key: string;
    text: string;
}

export interface BrowsableVersion {
    manual_version_key: string;
    faq_version_key: string;
    text: string;
    sub_versions: BrowsableSubProduct[];
    faq_sub_key?: string;
}

export interface BrowsableProduct {
    manual_key: string;
    faq_key: string;
    text: string;
    versions: BrowsableVersion[];
    is_sub_only?: string;
}

export interface AllProductCitationInsight {
    id: string;
    sourceLabel: string;
    productLabel: string;
    versionLabel: string;
    subProductLabel: string;
    searchScore: number | null;
    rerankerScore: number | null;
}

export interface AllProductScoreInsight {
    id: string;
    productLabel: string;
    confidence: number | null;
    citationCount: number;
    averageScore: number | null;
    maxScore: number | null;
    scoreSampleCount: number;
}

interface AllProductSuggestionsState {
    stage: AllProductValidationStage;
    matchedProducts: AllProductMatchedProduct[];
    message: string;
    isLocked: boolean;
    loadingPhase: SuggestionsLoadingPhase;
    searchCitations: AllProductCitationInsight[];
    scoreCandidates: AllProductScoreInsight[];
    skipReasonCode: string | null;
    pendingAction: SuggestionsActionIntent | null;
    allProducts: BrowsableProduct[];
}

const initialState: AllProductSuggestionsState = {
    stage: "idle",
    matchedProducts: [],
    message: "",
    isLocked: false,
    loadingPhase: null,
    searchCitations: [],
    scoreCandidates: [],
    skipReasonCode: null,
    pendingAction: null,
    allProducts: []
};

const allProductSuggestionsSlice = createSlice({
    name: "allProductSuggestions",
    initialState,
    reducers: {
        syncSuggestionsState(
            state,
            action: PayloadAction<{
                stage: AllProductValidationStage;
                matchedProducts: AllProductMatchedProduct[];
                message: string;
                isLocked: boolean;
                loadingPhase?: SuggestionsLoadingPhase;
                searchCitations?: AllProductCitationInsight[];
                scoreCandidates?: AllProductScoreInsight[];
                skipReasonCode?: string | null;
            }>
        ) {
            state.stage = action.payload.stage;
            state.matchedProducts = action.payload.matchedProducts;
            state.message = action.payload.message;
            state.isLocked = action.payload.isLocked;
            state.loadingPhase = action.payload.loadingPhase ?? null;
            state.searchCitations = action.payload.searchCitations ?? state.searchCitations;
            state.scoreCandidates = action.payload.scoreCandidates ?? state.scoreCandidates;
            state.skipReasonCode = action.payload.skipReasonCode !== undefined ? action.payload.skipReasonCode : state.skipReasonCode;
            if (action.payload.stage === "idle") {
                state.pendingAction = null;
                state.skipReasonCode = null;
            }
        },
        setPendingAction(state, action: PayloadAction<SuggestionsActionIntent>) {
            state.pendingAction = action.payload;
        },
        clearPendingAction(state) {
            state.pendingAction = null;
        },
        resetSuggestions(state) {
            state.stage = "idle";
            state.matchedProducts = [];
            state.message = "";
            state.isLocked = false;
            state.loadingPhase = null;
            state.searchCitations = [];
            state.scoreCandidates = [];
            state.skipReasonCode = null;
            state.pendingAction = null;
        },
        setAllProducts(state, action: PayloadAction<BrowsableProduct[]>) {
            state.allProducts = action.payload;
        }
    }
});

export const AllProductSuggestionsAction = allProductSuggestionsSlice.actions;
export default allProductSuggestionsSlice.reducer;
